'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Event, Events } from './index.model';
import { withSuspense } from '../../utils/withSuspense';
import { YaCounterId } from '../../lib/consts';

const SeoAnalyticsContext = createContext<{
  dispatch?: (event: string | Event) => boolean;
  saveCall?: (driverName: 'ga' | 'ym', methodName: any, params: any) => boolean;
}>({});

function SeoAnalyticsProviderInner({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const fullPath = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;

  const events: Events = {
    clickAppleApp: { ym: ['click.ios'], ga: ['app', 'clickIos', 'appIos'] },
    clickGooglePlayApp: {
      ym: ['click.and'],
      ga: ['app', 'clickAnd', 'appAnd'],
    },
  };

  function saveCall(driverName: 'ga' | 'ym', methodName: any, params: any) {
    try {
      if (typeof window === 'undefined') {
        return false;
      }
      const globalDriver =
        window[
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          {
            ga: 'gtag',
            ym: `yaCounter${YaCounterId}`,
          }[driverName]
        ];
      if (typeof globalDriver === 'undefined') {
        return false;
      }
      if (methodName) {
        globalDriver[methodName](...params);
      } else {
        globalDriver(...params);
      }
      return true;
    } catch (err) {
      console.error(err);
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return false;
    }
  }
  function ymSend(goalName?: string) {
    return saveCall('ym', 'reachGoal', [goalName]);
  }
  function gaSend(
    eventCategory?: string,
    eventAction?: string,
    eventLabel?: string
  ) {
    return saveCall('ga', null, [
      'event',
      eventAction,
      {
        event_category: eventCategory,
        event_label: eventLabel,
      },
    ]);
  }

  function dispatch(event: string | Event) {
    const drivers = {
      ym: 'ymSend',
      ga: 'gaSend',
    };

    const methods = {
      ymSend,
      gaSend,
    };
    const eventDictionary = typeof event === 'string' ? events[event] : event;
    if (!eventDictionary) {
      return false;
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const [driverName, driverFnName] of Object.entries(drivers)) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const payload = eventDictionary[driverName];
      if (payload) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        methods[driverFnName](...payload);
      }
    }
    return true;
  }

  useEffect(() => {
    saveCall('ym', 'hit', [fullPath]);
  }, [fullPath]);

  const contextValue = useMemo(() => ({ dispatch, saveCall }), []);
  return (
    <SeoAnalyticsContext.Provider value={contextValue}>
      {children}
    </SeoAnalyticsContext.Provider>
  );
}

export const SeoAnalyticsProvider = withSuspense(SeoAnalyticsProviderInner);

export function useSeoAnalytics() {
  return useContext(SeoAnalyticsContext);
}
